import React from "react";
import { Flex, Text } from "@chakra-ui/react";
import CustomApprovalModal from "@/shared/components/custom-modal";
import AppButton from "@/features/line-sheet-sets/app-button";
import PathPickBadge from "@/features/pathpick/components/ui/PathPickBadge";
import PathPickUsageCardSlot from "@/features/pathpick/components/usage/PathPickUsageCardSlot";
import usePathPickStockOrderUsage from "@/features/pathpick/components/usage/usePathPickStockOrderUsage";
import { PATHPICK_USAGE_WARNING_THRESHOLD } from "@/features/pathpick/constants";
import CustomIcon from "../../../../../public/icons-v1_1";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

interface TriggerButtonProps {
  onClick: () => void;
  // 한도 경고 배지 — 기본은 끔. 필요해지면 호출부에서 켠다.
  showNearLimitBadge?: boolean;
}

// 한도 경고 배지 — showNearLimitBadge로 켰을 때만 마운트된다.
// 판정에 usage(suspense 쿼리)가 필요하므로 컴포넌트로 떼어 뒀다. 트리거 버튼 본체에서
// 훅을 부르면 배지가 꺼져 있어도, 만료 사용자에게도 usage 요청·대기가 되살아난다.
// 판정 자체는 카드와 같은 훅에서 읽어 두 곳이 어긋나지 않는다.
const NearLimitBadge = () => {
  const { t } = useI18nHelper();
  const { isNearLimit } = usePathPickStockOrderUsage();

  if (!isNearLimit) {
    return null;
  }

  return (
    <PathPickBadge
      label={t("path_pick:usage_card.near_limit_badge", {
        threshold: PATHPICK_USAGE_WARNING_THRESHOLD,
      })}
      colorVariant={"yellow"}
    />
  );
};

// 사용량 모달을 여는 버튼 — 모바일에서만 노출하고, md 이상에서는 사용량 카드가 그대로 보인다.
// AppButton은 Tooltip으로 감싸여 있어 display 분기는 바깥 Flex에 건다.
//
// 카드를 감추면 한도 경고도 함께 사라지므로 버튼에 배지로 남길 수 있게 해뒀다(기본은 끔).
const TriggerButton = ({
  onClick,
  showNearLimitBadge = false,
}: TriggerButtonProps) => {
  const { t } = useI18nHelper();

  return (
    <Flex display={{ base: "flex", md: "none" }}>
      <AppButton
        variant={"white"}
        chakraProps={{ padding: "8px" }}
        onClick={onClick}
      >
        <Flex gap={"4px"} alignItems={"center"}>
          <CustomIcon
            src={"chart-bar"}
            width={16}
            height={16}
            color={"#444440"}
          />
          <Text textStyle={"body-sm-bold"} whiteSpace={"nowrap"}>
            {t("path_pick:usage_card.view_usage")}
          </Text>
          {showNearLimitBadge && <NearLimitBadge />}
        </Flex>
      </AppButton>
    </Flex>
  );
};

// 모바일에서 사용량 카드를 담는 모달 — 카드가 좁은 폭에서 세로로 스택되며 상단을
// 150px 넘게 잡아먹어 목록을 밀어내므로, 같은 카드를 버튼 뒤로 옮겨 담는다.
// 무엇이 들어갈지(사용량/만료)는 슬롯이 정하고, 읽기 전용이라 하단은 닫기 버튼 하나뿐이다.
function PathPickUsageModal({ isOpen, onClose }: Props) {
  const { t } = useI18nHelper();

  return (
    <CustomApprovalModal
      isOpen={isOpen}
      onClose={onClose}
      // minWidth가 maxWidth를 이기고, 가운데 정렬된 모달은 왼쪽으로 넘치면 스크롤로도
      // 닿을 수 없어 화면 폭으로 클램프한다.
      minWidth={"min(420px, 90dvw)"}
      isCentered={true}
    >
      <CustomApprovalModal.Header
        onClose={onClose}
        title={t("path_pick:usage_card.modal_title")}
      />
      <CustomApprovalModal.Body>
        <Flex flexDirection={"column"} gap={"16px"}>
          <PathPickUsageCardSlot />

          <Flex justifyContent={"flex-end"}>
            <AppButton variant={"primary"} onClick={onClose}>
              <Text textStyle={"body-sm-bold"}>
                {t("path_pick:filter.confirm")}
              </Text>
            </AppButton>
          </Flex>
        </Flex>
      </CustomApprovalModal.Body>
    </CustomApprovalModal>
  );
}

PathPickUsageModal.TriggerButton = TriggerButton;

export default PathPickUsageModal;
